import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  FormControl,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  Autocomplete,
} from '@mui/material'
import { useSnackbar } from '@/hooks/common/useSnackbar'

// Interface for Edit Transporter Dialog props
interface EditTransporterDialogProps {
  open: boolean
  onClose: () => void
  onSave: (data: {
    id: string
    email: string
    phone?: string
    first_name?: string
    last_name?: string
    user_ids: string[]
    transporterOwnerId?: string
  }) => Promise<void>
  transporter: {
    id: string
    email: string
    phone?: string
    first_name?: string
    last_name?: string
    user_id?: string
    transporterOwnerId?: string
  }
  users: any[]
}

// Edit Transporter Dialog Component
const EditTransporterDialog: React.FC<EditTransporterDialogProps> = ({
  open,
  onClose,
  onSave,
  transporter,
  users,
}) => {
  const [email, setEmail] = useState(transporter?.email || '')
  const [firstName, setFirstName] = useState(transporter?.first_name || '')
  const [lastName, setLastName] = useState(transporter?.last_name || '')
  const [phone, setPhone] = useState(transporter?.phone || '')
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])
  const [selectedOwner, setSelectedOwner] = useState<any>(null)
  const [isManualEntry, setIsManualEntry] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<{
    users?: string
    email?: string
    owner?: string
  }>({})
  const { showSuccess, showError } = useSnackbar()

  // Reset form when transporter changes
  useEffect(() => {
    if (transporter) {
      setEmail(transporter.email || '')
      setFirstName(transporter.first_name || '')
      setLastName(transporter.last_name || '')
      setPhone(transporter.phone || '')
      setIsManualEntry(!transporter.user_id)

      // Find the selected users
      if (transporter.user_id) {
        const user = users.find(u => u.id === transporter.user_id)
        setSelectedUsers(user ? [user] : [])
      } else {
        setSelectedUsers([])
      }

      // Find the owner
      if (transporter.transporterOwnerId) {
        const owner = users.find(u => u.id === transporter.transporterOwnerId)
        setSelectedOwner(owner || null)
      } else {
        setSelectedOwner(null)
      }
    }
  }, [transporter, users])

  useEffect(() => {
    if (selectedUsers.length === 1 && !isManualEntry) {
      const user = selectedUsers[0]
      setEmail(user.email || '')
      setFirstName(user.first_name || '')
      setLastName(user.last_name || '')
      setPhone(user.phone || '')
    } else if (selectedUsers.length > 1 && !isManualEntry) {
      // When multiple users are selected, we'll use the company name as the primary identifier
      setEmail('')
      setFirstName('')
      setLastName('')
      setPhone('')
    }
  }, [selectedUsers, isManualEntry])

  const validateForm = () => {
    const newErrors: { users?: string; email?: string; owner?: string } = {}

    if (!isManualEntry && selectedUsers.length === 0) {
      newErrors.users = 'Please select at least one user'
    }

    if (isManualEntry && (!email || !email.includes('@'))) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!selectedOwner && !isManualEntry && selectedUsers.length > 0) {
      newErrors.owner = 'Please select an owner for this transporter company'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setIsSubmitting(true)
      await onSave({
        id: transporter.id,
        email,
        first_name: firstName,
        last_name: lastName,
        phone,
        user_ids: isManualEntry ? [] : selectedUsers.map(user => user.id),
        transporterOwnerId: selectedOwner?.id || undefined,
      })
      onClose()
    } catch (error) {
      console.error('Error updating transporter:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Edit Transporter Group</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <FormControlLabel
            control={<Switch checked={isManualEntry} onChange={e => setIsManualEntry(e.target.checked)} />}
            label='Manage transporter manually (without user accounts)'
          />

          {!isManualEntry ? (
            <>
              <FormControl fullWidth error={!!errors.users}>
                <Autocomplete
                  multiple
                  id='users-select'
                  options={users}
                  getOptionLabel={option => `${option.email} (${option.first_name} ${option.last_name})`}
                  value={selectedUsers}
                  onChange={(_, newValue) => setSelectedUsers(newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={`${option.email} (${option.first_name} ${option.last_name})`}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={params => (
                    <TextField
                      {...params}
                      label='Select Users'
                      error={!!errors.users}
                      helperText={errors.users || 'Add or remove users from this transporter company'}
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth error={!!errors.owner}>
                <Autocomplete
                  id='owner-select'
                  options={users}
                  getOptionLabel={option => `${option.email} (${option.first_name} ${option.last_name})`}
                  value={selectedOwner}
                  onChange={(_, newValue) => setSelectedOwner(newValue)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label='Select Group Owner'
                      error={!!errors.owner}
                      helperText={errors.owner || 'Designate a primary owner for this transporter company'}
                    />
                  )}
                />
              </FormControl>

              {selectedUsers.length > 0 && (
                <Alert severity='info'>
                  All selected users will be able to access transporter features for assigned packages.
                </Alert>
              )}
            </>
          ) : (
            <Alert severity='warning'>
              Managing a transporter without user accounts means they won't be able to log in to the system. You'll
              need to manually handle their assignments.
            </Alert>
          )}

          <Divider />

          <Typography variant='subtitle1'>Transporter Contact Information</Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label='Email Address'
                fullWidth
                value={email}
                onChange={e => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                required={isManualEntry}
                disabled={!isManualEntry && selectedUsers.length === 1}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='First Name'
                fullWidth
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                disabled={!isManualEntry && selectedUsers.length === 1}
                helperText={
                  selectedUsers.length > 1 && !isManualEntry ? 'Enter a contact first name for the group' : ''
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Last Name'
                fullWidth
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                disabled={!isManualEntry && selectedUsers.length === 1}
                helperText={
                  selectedUsers.length > 1 && !isManualEntry ? 'Enter a contact last name for the group' : ''
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Phone Number'
                fullWidth
                value={phone}
                onChange={e => setPhone(e.target.value)}
                disabled={!isManualEntry && selectedUsers.length === 1}
                helperText={selectedUsers.length > 1 && !isManualEntry ? 'Enter a contact phone for the group' : ''}
              />
            </Grid>
          </Grid>

          {selectedUsers.length > 1 && (
            <Alert severity='info' sx={{ mt: 2 }}>
              When managing a transporter company with multiple users, make sure to provide contact information for the group.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose} variant='outlined' color='secondary'>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          color='primary'
          disabled={
            isSubmitting ||
            (!isManualEntry && selectedUsers.length === 0) ||
            (isManualEntry && !email.trim())
          }
        >
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditTransporterDialog
